/**
 * ShortLinkResolver — Phase 11
 *
 * Route:   /t/:code
 *
 * Receives a 7-char branded short code (e.g. /t/p7Kq2M), looks up the
 * target ticket token, and redirects via `navigate(..., {replace: true})`
 * so the back button skips this transient page.
 *
 * UX states:
 *   • Loading     → minimal brand pulse for ~200ms (no spinner flash)
 *   • Not Found   → friendly 404 card with CTA back to /events
 *   • Expired     → friendly 410 card with the event date for context
 *   • Server Err  → retry CTA
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, TicketX, CalendarX, RefreshCw } from 'lucide-react';
import { publicApi } from '../lib/api';

const STATES = {
  LOADING: 'loading',
  NOT_FOUND: 'not_found',
  EXPIRED: 'expired',
  ERROR: 'error',
};

export default function ShortLinkResolver() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.LOADING);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!code || code.length !== 7) {
        if (!cancelled) setState(STATES.NOT_FOUND);
        return;
      }
      try {
        const res = await publicApi.resolveShortLink(code);
        if (cancelled) return;
        const { found, expired, target_token, expires_at } = res?.data || {};
        if (!found) {
          setState(STATES.NOT_FOUND);
          return;
        }
        if (expired) {
          setExpiresAt(expires_at || null);
          setState(STATES.EXPIRED);
          return;
        }
        if (!target_token) {
          setState(STATES.ERROR);
          return;
        }
        // Success → redirect to the live ticket page
        navigate(`/tickets/${target_token}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setState(STATES.NOT_FOUND);
        else if (status === 410) setState(STATES.EXPIRED);
        else setState(STATES.ERROR);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [code, navigate]);

  // ── Loading: lightweight, no full-screen takeover ────────────────────────
  if (state === STATES.LOADING) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6"
        data-testid="shortlink-loading"
      >
        <Helmet><title>Opening your pass… | Scratch</title></Helmet>
        <div className="flex items-center gap-3 text-[#D4A843]">
          <Loader2 className="animate-spin" size={20} />
          <span className="font-['EB_Garamond'] text-lg">Opening your pass…</span>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (state === STATES.NOT_FOUND) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center"
        data-testid="shortlink-not-found"
      >
        <Helmet><title>Link not found | Scratch</title></Helmet>
        <div className="w-14 h-14 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center mb-5">
          <TicketX size={22} className="text-[#E05252]" />
        </div>
        <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">
          Link not found
        </h1>
        <p className="text-sm text-[#B9B9B9] mt-2 max-w-xs">
          This pass link is invalid or has been removed. Check the message again, or browse upcoming events below.
        </p>
        <Link
          to="/events"
          className="mt-6 bg-[#D4A843] text-[#0A0A0A] py-3 px-6 rounded-lg font-semibold tracking-wide hover:bg-[#C49A35] active:bg-[#B58A25] transition-colors"
          data-testid="shortlink-cta-events"
        >
          Browse Events
        </Link>
      </div>
    );
  }

  // ── Expired ──────────────────────────────────────────────────────────────
  if (state === STATES.EXPIRED) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center"
        data-testid="shortlink-expired"
      >
        <Helmet><title>Pass link expired | Scratch</title></Helmet>
        <div className="w-14 h-14 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center mb-5">
          <CalendarX size={22} className="text-[#D4A843]" />
        </div>
        <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">
          This pass link has expired
        </h1>
        <p className="text-sm text-[#B9B9B9] mt-2 max-w-sm">
          Short links remain valid for 7 days after the event. If you need a copy of an old ticket, sign in with your phone to view your booking history.
        </p>
        <div className="flex flex-wrap gap-3 mt-6 justify-center">
          <Link
            to="/account/bookings"
            className="bg-[#D4A843] text-[#0A0A0A] py-3 px-6 rounded-lg font-semibold tracking-wide hover:bg-[#C49A35] active:bg-[#B58A25] transition-colors"
            data-testid="shortlink-cta-bookings"
          >
            My Bookings
          </Link>
          <Link
            to="/events"
            className="bg-transparent border border-[#2A2A2A] text-[#B9B9B9] py-3 px-6 rounded-lg hover:border-[#D4A843] hover:text-[#D4A843] transition-colors"
          >
            Upcoming Events
          </Link>
        </div>
      </div>
    );
  }

  // ── Generic error ────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center"
      data-testid="shortlink-error"
    >
      <Helmet><title>Something went wrong | Scratch</title></Helmet>
      <div className="w-14 h-14 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center mb-5">
        <RefreshCw size={22} className="text-[#E05252]" />
      </div>
      <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">
        Could not open your pass
      </h1>
      <p className="text-sm text-[#B9B9B9] mt-2 max-w-xs">
        Please check your internet connection and try again. If this keeps happening, contact support.
      </p>
      <button
        onClick={() => { setState(STATES.LOADING); }}
        className="mt-6 bg-[#D4A843] text-[#0A0A0A] py-3 px-6 rounded-lg font-semibold tracking-wide hover:bg-[#C49A35] active:bg-[#B58A25] transition-colors"
        data-testid="shortlink-cta-retry"
      >
        Retry
      </button>
    </div>
  );
}
